import { useEffect, useState } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import { usePresence } from "contexts/PresenceContext";

/**
 * Hook to manage page presence tracking
 * Automatically tracks who is viewing the current page
 * @returns {{ viewers: Array, isConnected: boolean }} - List of other users viewing this page
 */
export const usePagePresence = () => {
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();
  const { currentPagePath, currentEntity } = usePresence();
  const [viewers, setViewers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  
  useEffect(() => {
    if (!socket || !currentPagePath || !currentUser) {
      return;
    }

    const userData = {
      id: currentUser.id,
      name: currentUser.Name,
      imageThumbnail: currentUser.ImageThumbnail,
    };

    // Join the page room with optional entity info
    const joinPayload = {
      pagePath: currentPagePath,
      user: userData,
    };

    // Add entity info if available
    if (currentEntity?.entityType && currentEntity?.entityId) {
      joinPayload.entityType = currentEntity.entityType;
      joinPayload.entityId = currentEntity.entityId;
    }

    socket.emit("page:join", joinPayload);
    setIsConnected(true);

    // Listen for presence updates
    const handlePresence = ({
      pagePath: receivedPath,
      viewers: receivedViewers,
    }) => {
      if (receivedPath === currentPagePath) {
        // Filter out current user from the viewers list
        const otherViewers = receivedViewers.filter(
          (v) => v.userId !== currentUser.id,
        );
        setViewers(otherViewers);
      }
    };

    socket.on("page:presence", handlePresence);

    // Cleanup: leave the room when page changes or unmounts
    return () => {
      const leavePayload = { pagePath: currentPagePath };

      // Include entity info if available
      if (currentEntity?.entityType && currentEntity?.entityId) {
        leavePayload.entityType = currentEntity.entityType;
        leavePayload.entityId = currentEntity.entityId;
      }

      socket.emit("page:leave", leavePayload);
      socket.off("page:presence", handlePresence);
      setIsConnected(false);
      setViewers([]);
    };
  }, [socket, currentPagePath, currentUser, currentEntity]);

  return { viewers, isConnected };
};

// Keep old name for backward compatibility but use new implementation
export const useAssignmentPresence = usePagePresence;
